import _ from 'lodash';
import moment from 'moment';
import AgeRangeReaches from 'common/domain/AgeRangeReaches';
import GenderReach from 'common/domain/GenderReach';
import DateRange from 'common/domain/DateRange';

export type MetricByDate = { [number]: number };

export type CampaignMetricsData = {
  runningCampaigns: number,
  reach: number,
  impressions: number,
  averageFrequency: number,
  genderDimension: GenderReach,
  ageGroup: AgeRangeReaches,
  reachByDate: MetricByDate,
  cumulativeImpressionsByDate: MetricByDate,
  spotsByDate: MetricByDate,
  progress: number,
  numberOfSpots: number,
  campaignsTotalCost: number,
}

export default class CampaignMetrics {
  runningCampaigns: number;
  reach: number;
  impressions: number;
  averageFrequency: number;
  genderDimension: GenderReach;
  ageGroup: AgeRangeReaches;
  reachByDate: MetricByDate;
  cumulativeImpressionsByDate: MetricByDate;
  spotsByDate: MetricByDate;
  progress: number;
  numberOfSpots: number;
  campaignsTotalCost: number;

  constructor(data: CampaignMetricsData) {
    this.runningCampaigns = data.runningCampaigns;
    this.reach = data.reach;
    this.impressions = data.impressions;
    this.averageFrequency = data.averageFrequency;
    this.genderDimension = data.genderDimension;
    this.ageGroup = data.ageGroup;
    this.reachByDate = data.reachByDate;
    this.cumulativeImpressionsByDate = data.cumulativeImpressionsByDate;
    this.spotsByDate = data.spotsByDate;
    this.progress = data.progress;
    this.numberOfSpots = data.numberOfSpots;
    this.campaignsTotalCost = data.campaignsTotalCost;
  }

  getFullDateRange(): DateRange | void {
    const timestamps = Object.keys(this.cumulativeImpressionsByDate);
    if (timestamps.length === 0) {
      return undefined;
    }

    const sortedTimestamp = timestamps.sort().map(timestamp => moment.unix(timestamp));
    const from = sortedTimestamp[0];
    const to = sortedTimestamp[sortedTimestamp.length - 1];

    return new DateRange({ from, to });
  }

  padMissingMetricsUntil(dateRange: DateRange): CampaignMetrics {
    const cumulativeImpressionsByDate = this._padMetricsByDateTo(this.cumulativeImpressionsByDate, dateRange.to.clone().startOf('day'));
    return new CampaignMetrics({ ...this, cumulativeImpressionsByDate });
  }

  _padMetricsByDateTo(metrics: MetricByDate, lastDate: moment) {
    const cumulativeImpressionsByDate = { ...this.cumulativeImpressionsByDate };

    const [lastTimestamp] = Object.keys(metrics).sort().slice(-1);
    const lastImpression = metrics[lastTimestamp];

    const lastMetricDate = moment.unix(lastTimestamp).startOf('day');
    const nbDaysToPad = lastDate.diff(lastMetricDate, 'days');

    if (nbDaysToPad < 1) {
      return cumulativeImpressionsByDate;
    }

    for (let day = 1; day <= nbDaysToPad; day++) {
      const padDate = lastMetricDate.clone().add(day, 'days').unix();
      cumulativeImpressionsByDate[padDate] = lastImpression;
    }

    return cumulativeImpressionsByDate;
  }

  static parse(toParse: any) {
    return new CampaignMetrics({
      runningCampaigns: toParse.runningCampaigns,
      reach: toParse.reach,
      impressions: toParse.impressions,
      averageFrequency: toParse.averageFrequency,
      genderDimension: GenderReach.parse(toParse.genderDimension),
      ageGroup: AgeRangeReaches.parse(toParse.ageGroup),
      reachByDate: convertKeysToUnixTimestamp(toParse.reachByDate),
      cumulativeImpressionsByDate: convertKeysToUnixTimestamp(toParse.cumulativeImpressionsByDate),
      spotsByDate: convertKeysToUnixTimestamp(toParse.spotsByDate),
      progress: toParse.progress,
      numberOfSpots: toParse.numberOfSpots,
      campaignsTotalCost: toParse.campaignsTotalCost,
    });
  }

  static areEquals(a?: CampaignMetrics, b?: CampaignMetrics) {
    return _.isEqual(a, b);
  }
}

export function convertKeysToUnixTimestamp(valueByDate: Object) {
  const obj = {};

  _.forEach(valueByDate, (value, key) => {
    obj[moment(key).unix()] = value;
  });

  return obj;
}




// WEBPACK FOOTER //
// ./src/common/modules/campaign/domain/CampaignMetrics.js